import SmartBanner from '@/components/Common/SmartBanner/SmartBanner';
import { GlobalStoreCtx } from '@/stores/GlobalStore';
import ScrollStore from '@/stores/ScrollStore';
import { call } from '@/utils/yambda';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { always, cond, equals, prop } from 'ramda';
import React, { useContext } from 'react';

import styles from './FixedComponent.module.scss';

const getPositionClass = cond([
  [equals('top'), always(styles.top)],
  [equals('bottom'), always(styles.bottom)],
]);

const getSmartBannerClass = (withSmartBanner: boolean): string | null => {
  return withSmartBanner ? styles.withSmartBanner : null;
};

interface IFixedComponentProps {
  type: 'top' | 'bottom';
  hiddenDirection?: 'up' | 'down';
  isAllowedPcScroll?: boolean;
  scrollStore?: ScrollStore;
  withSmartBanner?: boolean;
  /** `AppDevice`가 참이어도 표시? */
  alwaysDisplay?: boolean;
  className?: string;
  hiddenClassName?: string;
  style?: React.CSSProperties;
}

/**
 * 모바일에서 보여졌다가 스크롤 내리면 사라지는 아이들 (ex. 스마트배너)
 */
const FixedComponent: React.FC<IFixedComponentProps> = ({
  type,
  scrollStore,
  hiddenDirection = 'down',
  isAllowedPcScroll = false,
  withSmartBanner: smartBanner = true,
  alwaysDisplay,
  className,
  hiddenClassName,
  style: propStyle,
  children,
}) => {
  const globalStore = useContext(GlobalStoreCtx);

  const appDevice = prop('AppDevice', globalStore);
  const isPc = prop('IsPc', globalStore);
  const withSmartBanner = type === 'top' && smartBanner && prop('ShowSmartBanner', globalStore);
  const isGoingDown = prop('IsGoingDown', scrollStore!);
  const isGoingUp = prop('IsGoingUp', scrollStore!);
  const isPcWindowWidth = global['innerWidth'] >= 768;
  const hideClass = call((): string | null => {
    if (alwaysDisplay) {
      return null;
    }

    if (!isAllowedPcScroll && (isPc || isPcWindowWidth)) {
      return null;
    }

    return appDevice || (isGoingDown && hiddenDirection === 'down') || (isGoingUp && hiddenDirection === 'up')
      ? clsx(styles.hidden, hiddenClassName)
      : null;
  });

  return (
    <section
      className={clsx(
        styles.container,
        getSmartBannerClass(withSmartBanner),
        getPositionClass(type),
        hideClass,
        className,
      )}
      style={propStyle}
    >
      {withSmartBanner && <SmartBanner />}
      {children && React.Children.map(React.Children.toArray(children), (child) => child)}
      {hideClass && <div className={styles.hider} />}
    </section>
  );
};

export default observer(FixedComponent);
